'use client';

import React, { createContext, useContext, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import UserEmailAlertsRepository from '../infrastructure/UserEmailAlertsRepository';
import { useSessionContext } from '@users/session/SessionContext';
import { AlertManager } from '@dealroadshow/uikit-alert';
import { messageCodes } from '@framework/message/messages';
import { getMessage, getErrorMessage } from '@framework/message/Mapper/getMessage';
import { QuestionAlertsType } from '../ui/QuestionAlertsTypeSwitcher';
import { IFileUploadNotificationSettingsPayload } from '../domain/vo/FileUploadEmailAlerts';
import { IStagingFileUploadNotificationSettingsPayload } from '../domain/vo/StagingFileUploadEmailAlerts';
import { IDigestNotificationSettingsPayload } from '../domain/vo/DigestEmailAlerts';
import { IAccessRequestNotificationSettingsPayload } from '../domain/vo/AccessRequestEmailAlerts';
import { IQuestionsNotificationSettingsPayload, IQuestionsRealtimeNotificationSettingsPayload } from '../domain/vo/QuestionsEmailAlerts';

type AllOrNone<T> = T | { [K in keyof T]?: never };

const useEmailAlerts = () => {
  const [isFetching, setIsFetching] = useState(false);
  const { currentUser } = useSessionContext() as any;
  const container = getContainer();

  const updateSettings = async ({
      dataroomName,
      userEmails,
      isUploadAlertsDirty,
      uploadNotificationSettings,
      uploadUserIds,
      isStagingUploadAlertsDirty,
      stagingUploadNotificationSettings,
      stagingUploadUserIds,
      isDigestAlertsDirty,
      digestNotificationSettings,
      digestUserIds,
      isAccessRequestAlertsDirty,
      accessRequestNotificationSettings,
      accessRequestUserIds,
      isQuestionAlertsDirty,
      questionNotificationSettings,
      questionRealtimeNotificationSettings,
      questionNotificationTypeValue,
      isQuestionManager,
      questionUserIds,
    }: {
    dataroomName: string,
    userEmails?: string[],
  } & AllOrNone<{
    isUploadAlertsDirty: boolean,
    uploadNotificationSettings: IFileUploadNotificationSettingsPayload,
    uploadUserIds?: number[],
  }> & AllOrNone<{
    isStagingUploadAlertsDirty: boolean,
    stagingUploadNotificationSettings: IStagingFileUploadNotificationSettingsPayload,
    stagingUploadUserIds?: number[],
  }> & AllOrNone<{
    isDigestAlertsDirty: boolean,
    digestNotificationSettings: IDigestNotificationSettingsPayload,
    digestUserIds?: number[],
  }> & AllOrNone<{
    isAccessRequestAlertsDirty: boolean,
    accessRequestNotificationSettings: IAccessRequestNotificationSettingsPayload,
    accessRequestUserIds?: number[],
  }> & AllOrNone<{
    isQuestionAlertsDirty: boolean,
    questionNotificationSettings: IQuestionsNotificationSettingsPayload,
    questionRealtimeNotificationSettings: IQuestionsRealtimeNotificationSettingsPayload,
    questionNotificationTypeValue: QuestionAlertsType,
    isQuestionManager: boolean,
    questionUserIds?: number[],
  }>) => {
    const { email: currentUserEmail } = currentUser || {};

    const emailsString = userEmails?.join(', ') || currentUserEmail;

    if (!emailsString) {
      return false;
    }

    setIsFetching(true);

    try {
      const userEmailAlertsRepository = container.get<UserEmailAlertsRepository>(UserEmailAlertsRepository);

      await Promise.all([
        ...(isUploadAlertsDirty ? [
          await userEmailAlertsRepository.updateUploadNotificationSettings(
            getUploadNotificationSettings(
            dataroomName,
            uploadUserIds!,
            uploadNotificationSettings,
          )),
        ] : []),
        ...(isStagingUploadAlertsDirty ? [
          await userEmailAlertsRepository.updateStagingUploadNotificationSettings(
            getStagingUploadNotificationSettings(
            dataroomName,
            stagingUploadUserIds!,
            stagingUploadNotificationSettings,
          )),
        ] : []),
        ...(isDigestAlertsDirty ? [
          await userEmailAlertsRepository.updateDigestNotificationSettings(
            getDigestNotificationSettings(
            dataroomName,
            digestUserIds!,
            digestNotificationSettings,
          )),
        ] : []),
        ...(isAccessRequestAlertsDirty ? [
          await userEmailAlertsRepository.updateAccessRequestNotificationSettings(
            getAccessRequestNotificationSettings(
            dataroomName,
            accessRequestUserIds!,
            accessRequestNotificationSettings,
          )),
        ] : []),
        ...(isQuestionAlertsDirty ? [
          await userEmailAlertsRepository.updateQuestionNotificationSettings(
            getQuestionNotificationSettings(
            dataroomName,
            questionUserIds!,
            questionNotificationSettings,
            questionRealtimeNotificationSettings,
            questionNotificationTypeValue,
            isQuestionManager,
          )),
        ] : []),
      ]);

      AlertManager.success(getMessage(messageCodes.DATAROOM_ALERTS_UPDATE, { userString: emailsString }));
      return true;
    } catch (error) {
      AlertManager.error(getErrorMessage(error, { userString: emailsString }));
      return false;
    } finally {
      setIsFetching(false);
    }
  };

  const getUploadNotificationSettings = (
    dataroomName: string,
    userIds: number[],
    {
      isRealTime,
      isDaily,
      isWeekly,
      weeklyTime,
      weeklyDay,
      dailyTime,
    }: IFileUploadNotificationSettingsPayload,
  ) => ({
    dataroomName,
    ...(userIds ? { userIds } : {}),
    notifications: {
      realtime: {
        enabled: isRealTime,
      },
      daily: {
        enabled: isDaily,
        time: dailyTime,
      },
      weekly: {
        enabled: isWeekly,
        day: weeklyDay,
        time: weeklyTime,
      },
    },
  });

  const getStagingUploadNotificationSettings = (dataroomName: string, userIds: number[], {
    isRealTime,
    isDaily,
    isWeekly,
    weeklyTime,
    weeklyDay,
    dailyTime,
  }: IStagingFileUploadNotificationSettingsPayload) => ({
    dataroomName,
    ...(userIds ? { userIds } : {}),
    notifications: {
      stagingRealtime: {
        enabled: isRealTime,
      },
      stagingDaily: {
        enabled: isDaily,
        time: dailyTime,
      },
      stagingWeekly: {
        enabled: isWeekly,
        day: weeklyDay,
        time: weeklyTime,
      },
    },
  });

  const getDigestNotificationSettings = (dataroomName: string, userIds: number[], {
    isDigestDaily,
    isDigestWeekly,
    digestDailyTime,
    digestWeeklyTime,
    digestWeeklyDay,
  }: IDigestNotificationSettingsPayload) => ({
    dataroomName,
    ...(userIds ? { userIds } : {}),
    notifications: {
      digestDaily: {
        enabled: isDigestDaily,
        time: digestDailyTime,
      },
      digestWeekly: {
        enabled: isDigestWeekly,
        day: digestWeeklyDay,
        time: digestWeeklyTime,
      },
    },
  });

  const getAccessRequestNotificationSettings = (dataroomName: string, userIds: number[], {
    isRealTime,
    isDaily,
    isWeekly,
    weeklyTime,
    weeklyDay,
    dailyTime,
  }: IAccessRequestNotificationSettingsPayload) => ({
    dataroomName,
    ...(userIds ? { userIds } : {}),
    notifications: {
      accessRequestRealtime: {
        enabled: isRealTime,
      },
      accessRequestDaily: {
        enabled: isDaily,
        time: dailyTime,
      },
      accessRequestWeekly: {
        enabled: isWeekly,
        day: weeklyDay,
        time: weeklyTime,
      },
    },
  });

  const getQuestionNotificationSettings = (
    dataroomName: string,
    userIds: number[],
    {
      isQuestionDaily,
      isQuestionWeekly,
      isQuestionRealTime,
      questionDailyTime,
      questionWeeklyTime,
      questionWeeklyDay,
    }: IQuestionsNotificationSettingsPayload,
    {
      questionSubmitted,
      replySubmitted,
      noteSubmitted,
      questionAssigned,
    }: IQuestionsRealtimeNotificationSettingsPayload,
    questionNotificationTypeValue: QuestionAlertsType,
    isQuestionManager: boolean,
  ) => ({
    dataroomName,
    ...(userIds ? { userIds } : {}),
    notifications: {
      daily: {
        enabled: isQuestionDaily,
        time: [questionDailyTime],
      },
      weekly: {
        enabled: isQuestionWeekly,
        day: questionWeeklyDay,
        time: questionWeeklyTime,
      },
      allQuestions: !!questionNotificationTypeValue,
      realtime: {
        enabled: isQuestionRealTime,
        realtime: {
          questionSubmitted: isQuestionRealTime && questionSubmitted,
          replySubmitted: isQuestionRealTime && replySubmitted,
          noteSubmitted: isQuestionRealTime && isQuestionManager && noteSubmitted,
          questionAssigned: isQuestionRealTime && isQuestionManager && questionAssigned,
        },
      },
    },
  });

  return {
    isFetching,
    updateSettings,
  };
};

type EmailAlertsContextType = ReturnType<typeof useEmailAlerts>;

export const EmailAlertsContext = createContext<EmailAlertsContextType | null>(null);

export const useEmailAlertsContext = () => {
  const context = useContext(EmailAlertsContext);
  if (!context) {
    throw new Error('useEmailAlertsContext must be used within a EmailAlertsContextProvider');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const EmailAlertsContextProvider = ({ children }: IProps) => (
  <EmailAlertsContext.Provider value={ useEmailAlerts() }>
    { children }
  </EmailAlertsContext.Provider>
);

export default EmailAlertsContextProvider;
